import {
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Tune } from "@mui/icons-material";
import FileSearch from "components/fileStorage/FileSearch";
import PaginationControls from "components/fileStorage/PaginationControls";
import UserManagementPanel from "components/userManagement/UserManagementPanel";
import UnsavedPlatformDraftsDialog from "components/userManagement/UnsavedPlatformDraftsDialog";
import {
  CREATE_MODE_DRAFT_KEY,
  type UserManagementDrafts,
  PLATFORM_SELECTED_USER_STORAGE_KEY,
  clearPersistedPlatformUserSelection,
  clearPlatformUserDrafts,
  hasAnyPlatformUserDrafts,
  loadPlatformUserDrafts,
  mergeDraftClientNamesToSession,
  savePlatformUserDrafts,
  summarizeDraftsForDialog,
} from "userManagement/platformDraftsStorage";
import UserManagementTable from "components/userManagement/UserManagementTable";
import BackendUserTable from "components/userManagement/BackendUserTable";
import BackendUserPanel from "components/userManagement/BackendUserPanel";
import {
  GetAdminUsersParams,
  useGetAdminUsersQuery,
  useGetBackendUsersQuery,
} from "features/adminUsers/adminUsers";
import { useAppDispatch, useAppSelector } from "features/store";
import { pushNotification } from "features/notificationSlice";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BackendUser, PlatformUser } from "types/interfaces/adminUsers";
import Layout from "../components/Layout";
import { TABLE_PAGE_CONTAINER_HEIGHT } from "config";

const DEFAULT_PAGE_SIZE = 50;
const MODE_STORAGE_KEY = "userManagementMode";
const BACKEND_SELECTED_KEY = "userManagementSelectedBackendId";

export default function UserManagementPage() {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentUser = useAppSelector((state) => state.auth.user);
  const pp = currentUser?.platform_profile;
  const canCreateUsers = Boolean(pp?.is_superuser);
  const isSuperUser = Boolean(pp?.is_superuser);
  const [mode, setMode] = useState<"web" | "backend">("web");
  const [sortBy, setSortBy] = useState<
    GetAdminUsersParams["sort_by"] | undefined
  >(undefined);
  const [sortOrder, setSortOrder] =
    useState<GetAdminUsersParams["sort_order"]>("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined);
  const [backendLabelFilter, setBackendLabelFilter] = useState<
    "migration" | "integration" | "sftp" | undefined
  >(undefined);
  const [createMode, setCreateMode] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const showToast = (message: string, severity: "success" | "error") => {
    dispatch(pushNotification({ message, severity }));
  };
  const createdUserRef = useRef<PlatformUser | null>(null);
  const createdAtRef = useRef<number | null>(null);
  const [drafts, setDrafts] = useState<Record<number, UserManagementDrafts>>(
    loadPlatformUserDrafts,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [staleDraftsDialogOpen, setStaleDraftsDialogOpen] = useState(() =>
    hasAnyPlatformUserDrafts(loadPlatformUserDrafts()),
  );
  const [platformPanelUnsaved, setPlatformPanelUnsaved] = useState(false);
  const [panelResetKey, setPanelResetKey] = useState(0);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [mobileOptionsDrawerOpen, setMobileOptionsDrawerOpen] = useState(false);
  const pendingLeaveDraftKeyRef = useRef<number | null>(null);
  const pendingLeaveActionRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!isSuperUser) return;
    try {
      const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
      if (stored === "backend" || stored === "web") {
        setMode(stored);
      }
    } catch {}
  }, [isSuperUser]);

  useEffect(() => {
    if (mode !== "backend") return;
    try {
      const raw = window.localStorage.getItem(BACKEND_SELECTED_KEY);
      const id = raw ? parseInt(raw, 10) : NaN;
      if (!Number.isNaN(id)) {
        setSelectedUserId(id);
      }
    } catch {}
  }, [mode]);

  useEffect(() => {
    if (mode !== "web") return;
    try {
      const raw = window.localStorage.getItem(
        PLATFORM_SELECTED_USER_STORAGE_KEY,
      );
      const id = raw ? parseInt(raw, 10) : NaN;
      if (!Number.isNaN(id)) {
        setSelectedUserId(id);
      }
    } catch {}
  }, [mode]);

  const { data, isLoading, isFetching } = useGetAdminUsersQuery(
    {
      page,
      search: search || undefined,
      page_size: pageSize,
      role: roleFilter,
      ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
    },
    { refetchOnMountOrArgChange: true },
  );

  const users = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;

  const {
    data: backendData,
    isLoading: isBackendLoading,
    isFetching: isBackendFetching,
  } = useGetBackendUsersQuery(
    {
      page,
      search: search || undefined,
      page_size: pageSize,
      ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
    },
    { skip: mode !== "backend" },
  );

  const backendUsersRaw = backendData?.results ?? [];
  const backendUsers =
    backendLabelFilter === "migration"
      ? backendUsersRaw.filter((u) => u.folder_count > 0)
      : backendLabelFilter === "integration"
        ? backendUsersRaw.filter((u) => u.integration_count > 0)
        : backendLabelFilter === "sftp"
          ? backendUsersRaw.filter((u) => u.sftp_count > 0)
          : backendUsersRaw;
  const backendTotalItems = backendData?.count ?? 0;
  const backendTotalPages = Math.ceil(backendTotalItems / pageSize) || 1;

  const handleManageBackend = (user: BackendUser) => {
    setSelectedUserId((prev) => {
      const next = prev === user.id ? null : user.id;
      try {
        if (next == null) {
          window.localStorage.removeItem(BACKEND_SELECTED_KEY);
        } else {
          window.localStorage.setItem(BACKEND_SELECTED_KEY, String(next));
        }
      } catch {}
      return next;
    });
    setCreateMode(false);
  };

  const panelUser: PlatformUser | null = createMode
    ? null
    : selectedUserId == null
      ? null
      : (users.find((u) => u.id === selectedUserId) ??
        createdUserRef.current ??
        null);
  const panelOpen = createMode || selectedUserId !== null;

  useEffect(() => {
    if (!panelOpen || mode !== "web") {
      setPlatformPanelUnsaved(false);
    }
  }, [panelOpen, mode]);

  const closePlatformPanel = useCallback(() => {
    setCreateMode(false);
    setSelectedUserId(null);
    createdUserRef.current = null;
    createdAtRef.current = null;
    clearPersistedPlatformUserSelection();
  }, []);

  const closeBackendPanel = useCallback(() => {
    setCreateMode(false);
    setSelectedUserId(null);
    try {
      window.localStorage.removeItem(BACKEND_SELECTED_KEY);
    } catch {}
  }, []);

  const onPlatformUnsavedChange = useCallback((dirty: boolean) => {
    setPlatformPanelUnsaved(dirty);
  }, []);

  const draftClientNameByUserId = useMemo(() => {
    const m: Partial<Record<number, string>> = {};
    for (const u of users) {
      const name = (u.first_name || u.username || "").trim();
      m[u.id] = name || `User ID ${u.id}`;
    }
    if (panelUser) {
      const name = (panelUser.first_name || panelUser.username || "").trim();
      m[panelUser.id] = name || `User ID ${panelUser.id}`;
    }
    return m;
  }, [users, panelUser]);

  const staleDraftSummaryRows = useMemo(
    () =>
      staleDraftsDialogOpen
        ? summarizeDraftsForDialog(drafts, draftClientNameByUserId)
        : [],
    [drafts, draftClientNameByUserId, staleDraftsDialogOpen],
  );

  const leaveDraftSummaryRows = useMemo(() => {
    if (!leaveDialogOpen) return [];
    const pendingKey = pendingLeaveDraftKeyRef.current;
    if (pendingKey == null) return [];
    return summarizeDraftsForDialog(drafts, draftClientNameByUserId).filter(
      (row) => row.userId === pendingKey,
    );
  }, [drafts, draftClientNameByUserId, leaveDialogOpen]);

  useEffect(() => {
    if (mode !== "web") return;
    const updates: Record<number, string> = {};
    for (const u of users) {
      const name = (u.first_name || u.username || "").trim();
      updates[u.id] = name || `User ID ${u.id}`;
    }
    if (panelUser) {
      const name = (panelUser.first_name || panelUser.username || "").trim();
      updates[panelUser.id] = name || `User ID ${panelUser.id}`;
    }
    if (Object.keys(updates).length > 0) {
      mergeDraftClientNamesToSession(updates);
    }
  }, [mode, users, panelUser]);

  const handleUserArchived = () => {
    showToast("User archive status updated.", "success");
  };

  const handlePlatformDraftsChange = useCallback(
    (
      updater: (
        prev: Record<number, UserManagementDrafts>,
      ) => Record<number, UserManagementDrafts>,
    ) => {
      setDrafts((prev) => {
        const nextRaw = updater(prev);
        const ids = Object.keys(nextRaw)
          .map((k) => parseInt(k, 10))
          .filter((id) => !Number.isNaN(id))
          .sort((a, b) => a - b);
        const preferredKey =
          ids.find((id) => id === (selectedUserId ?? CREATE_MODE_DRAFT_KEY)) ??
          ids[ids.length - 1];
        const next =
          preferredKey == null ? {} : { [preferredKey]: nextRaw[preferredKey] };
        savePlatformUserDrafts(next);
        return next;
      });
    },
    [selectedUserId],
  );

  const handleDiscardAndLeavePlatform = useCallback(() => {
    const key = pendingLeaveDraftKeyRef.current;
    const fn = pendingLeaveActionRef.current;
    pendingLeaveDraftKeyRef.current = null;
    pendingLeaveActionRef.current = null;
    setLeaveDialogOpen(false);
    if (key != null) {
      handlePlatformDraftsChange((prev) => {
        const { [key]: _, ...rest } = prev;
        return rest;
      });
    }
    setPanelResetKey((k) => k + 1);
    if (fn) {
      fn();
      return;
    }
    // Safety fallback: Discard must always close the drawer.
    closePlatformPanel();
  }, [handlePlatformDraftsChange, closePlatformPanel]);

  const requestClosePlatformPanel = useCallback(() => {
    if (!platformPanelUnsaved) {
      closePlatformPanel();
      return;
    }
    pendingLeaveDraftKeyRef.current = createMode
      ? CREATE_MODE_DRAFT_KEY
      : selectedUserId!;
    pendingLeaveActionRef.current = closePlatformPanel;
    setLeaveDialogOpen(true);
  }, [platformPanelUnsaved, createMode, selectedUserId, closePlatformPanel]);

  const handleMobilePanelDialogClose = useCallback(() => {
    if (mode === "web") {
      requestClosePlatformPanel();
    } else {
      closeBackendPanel();
    }
  }, [mode, requestClosePlatformPanel, closeBackendPanel]);

  useEffect(() => {
    if (!platformPanelUnsaved || mode !== "web" || !panelOpen) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [platformPanelUnsaved, mode, panelOpen]);

  const handleManagePlatform = useCallback(
    (user: PlatformUser) => {
      if (
        createdUserRef.current &&
        user.id !== createdUserRef.current.id &&
        createdAtRef.current != null &&
        Date.now() - createdAtRef.current < 1500
      ) {
        return;
      }

      const togglingOff = selectedUserId === user.id;
      const willChangeContext =
        createMode ||
        togglingOff ||
        (selectedUserId != null && selectedUserId !== user.id);

      const run = () => {
        createdUserRef.current = null;
        createdAtRef.current = null;
        setSelectedUserId((prev) => {
          const next = prev === user.id ? null : user.id;
          try {
            if (next == null) {
              clearPersistedPlatformUserSelection();
            } else {
              window.localStorage.setItem(
                PLATFORM_SELECTED_USER_STORAGE_KEY,
                String(next),
              );
            }
          } catch {}
          return next;
        });
        setCreateMode(false);
      };

      if (
        mode === "web" &&
        panelOpen &&
        platformPanelUnsaved &&
        willChangeContext
      ) {
        pendingLeaveDraftKeyRef.current = createMode
          ? CREATE_MODE_DRAFT_KEY
          : selectedUserId!;
        pendingLeaveActionRef.current = run;
        setLeaveDialogOpen(true);
        return;
      }
      run();
    },
    [mode, panelOpen, platformPanelUnsaved, createMode, selectedUserId],
  );

  const handleSortChange = (field: GetAdminUsersParams["sort_by"]) => {
    if (sortBy === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else if (sortOrder === "desc") {
        setSortBy(undefined);
        setSortOrder("asc");
      }
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(1);
  };

  return (
    <Layout title="User Management" content="User Management">
      <Container
        component="main"
        maxWidth={false}
        sx={{
          py: 3,
          width: "100%",
          boxSizing: "border-box",
          height: TABLE_PAGE_CONTAINER_HEIGHT,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box mb={2} display="flex" flexDirection="column" gap={1}>
          {isMobile ? (
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FileSearch
                  onSearch={(term) => {
                    setSearch(term || "");
                    setPage(1);
                  }}
                  placeholder="Search users..."
                  autoComplete="off"
                />
              </Box>
              <IconButton
                color="primary"
                aria-label="Open filters and actions"
                onClick={() => setMobileOptionsDrawerOpen(true)}
                sx={{
                  flexShrink: 0,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                }}
              >
                <Tune />
              </IconButton>
            </Box>
          ) : (
            <>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap={2}
                width="100%"
              >
                <Box
                  display="flex"
                  flexDirection="row"
                  alignItems="center"
                  gap={2}
                  sx={{ flex: "0 1 auto", minWidth: 0 }}
                >
                  <Box
                    sx={{
                      width: 400,
                      maxWidth: "100%",
                      minWidth: 200,
                      flexShrink: 1,
                    }}
                  >
                    <FileSearch
                      onSearch={(term) => {
                        setSearch(term || "");
                        setPage(1);
                      }}
                      placeholder="Search users..."
                      autoComplete="off"
                    />
                  </Box>
                  <Tooltip
                    title={
                      !canCreateUsers
                        ? "Super User required to create users."
                        : ""
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                          if (!canCreateUsers) return;
                          const run = () => {
                            createdUserRef.current = null;
                            createdAtRef.current = null;
                            clearPersistedPlatformUserSelection();
                            setCreateMode(true);
                            setSelectedUserId(null);
                          };
                          if (
                            mode === "web" &&
                            panelOpen &&
                            platformPanelUnsaved &&
                            (!createMode || selectedUserId !== null)
                          ) {
                            pendingLeaveDraftKeyRef.current = createMode
                              ? CREATE_MODE_DRAFT_KEY
                              : selectedUserId!;
                            pendingLeaveActionRef.current = run;
                            setLeaveDialogOpen(true);
                            return;
                          }
                          run();
                        }}
                        disabled={!canCreateUsers}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          minHeight: 42,
                          borderRadius: 1,
                          flexShrink: 0,
                        }}
                      >
                        + New User
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
                {isSuperUser && (
                  <Box sx={{ ml: "auto", flexShrink: 0 }}>
                    <ToggleButtonGroup
                      value={mode}
                      exclusive
                      onChange={(_, value) => {
                        if (!value) return;
                        const apply = () => {
                          setMode(value);
                          try {
                            window.localStorage.setItem(
                              MODE_STORAGE_KEY,
                              value,
                            );
                            if (value === "backend") {
                              clearPersistedPlatformUserSelection();
                            } else {
                              window.localStorage.removeItem(
                                BACKEND_SELECTED_KEY,
                              );
                            }
                          } catch {}
                          setSelectedUserId(null);
                          setCreateMode(false);
                          setPage(1);
                          setRoleFilter(undefined);
                          setBackendLabelFilter(undefined);
                        };
                        if (
                          mode === "web" &&
                          panelOpen &&
                          platformPanelUnsaved
                        ) {
                          pendingLeaveDraftKeyRef.current = createMode
                            ? CREATE_MODE_DRAFT_KEY
                            : selectedUserId!;
                          pendingLeaveActionRef.current = apply;
                          setLeaveDialogOpen(true);
                          return;
                        }
                        apply();
                      }}
                      size="small"
                      sx={{
                        "& .MuiToggleButton-root": {
                          minHeight: 42,
                          textTransform: "none",
                          fontWeight: 600,
                        },
                      }}
                    >
                      <ToggleButton value="web">Web Users</ToggleButton>
                      <ToggleButton value="backend">Backend Users</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                  gap: 1,
                  overflowX: "auto",
                  pb: 0.5,
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "thin",
                }}
              >
                {mode === "web"
                  ? (
                      [
                        { value: "data_storage", label: "Data Storage" },
                        {
                          value: "integration_dashboard",
                          label: "Integration",
                        },
                        { value: "ai_agent", label: "AI Agent" },
                        { value: "calibrate_manager", label: "Manager" },
                        { value: "superuser", label: "Super Admin" },
                        { value: "top_folder_zip", label: "Top Folder ZIP" },
                        { value: "archived_pending", label: "Archived pending" },
                        { value: "archived", label: "Archived" },
                      ] as const
                    ).map(({ value, label }) => (
                      <Chip
                        key={value}
                        label={label}
                        size="small"
                        clickable
                        onClick={() => {
                          setRoleFilter((prev) =>
                            prev === value ? undefined : value,
                          );
                          setPage(1);
                        }}
                        sx={{
                          flexShrink: 0,
                          fontWeight: 600,
                          fontSize: 12,
                          bgcolor:
                            roleFilter === value ? "primary.main" : undefined,
                          color:
                            roleFilter === value
                              ? "primary.contrastText"
                              : undefined,
                          "& .MuiChip-label": { px: 1.25 },
                        }}
                      />
                    ))
                  : (
                      [
                        { key: "migration" as const, label: "Migration" },
                        { key: "integration" as const, label: "Integration" },
                        { key: "sftp" as const, label: "SFTP" },
                      ] as const
                    ).map(({ key, label }) => (
                      <Chip
                        key={key}
                        label={label}
                        size="small"
                        clickable
                        onClick={() => {
                          setBackendLabelFilter((prev) =>
                            prev === key ? undefined : key,
                          );
                          setPage(1);
                        }}
                        sx={{
                          flexShrink: 0,
                          fontWeight: 600,
                          fontSize: 12,
                          bgcolor:
                            backendLabelFilter === key
                              ? "primary.main"
                              : undefined,
                          color:
                            backendLabelFilter === key
                              ? "primary.contrastText"
                              : undefined,
                          "& .MuiChip-label": { px: 1.25 },
                        }}
                      />
                    ))}
              </Box>
            </>
          )}
        </Box>

        <Box
          sx={{
            position: "relative",
            display: "flex",
            flex: 1,
            minHeight: 0,
            borderRadius: { xs: isMobile && panelOpen ? 0 : 1, sm: 1 },
            boxShadow: { xs: isMobile && panelOpen ? 0 : 1, sm: 1 },
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
            bgcolor: "background.paper",
          }}
        >
          <Backdrop
            open={isSaving}
            sx={{
              position: isMobile && panelOpen ? "fixed" : "absolute",
              zIndex: isMobile && panelOpen ? theme.zIndex.modal + 2 : 1300,
              inset: 0,
              borderRadius: isMobile && panelOpen ? 0 : 1,
              bgcolor: (t) => alpha(t.palette.background.paper, 0.85),
            }}
          >
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={1.5}
            >
              <CircularProgress sx={{ color: "primary.main" }} />
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
              >
                Saving…
              </Typography>
            </Box>
          </Backdrop>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: { xs: panelOpen ? "none" : "flex", sm: "flex" },
              flexDirection: "column",
            }}
          >
            {mode === "web" ? (
              <UserManagementTable
                users={users}
                isLoading={isLoading}
                isFetching={isFetching}
                selectedUserId={selectedUserId ?? undefined}
                onManage={handleManagePlatform}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
                hasUnsavedChangesForUser={(userId) => {
                  const d = drafts[userId];
                  if (!d) return false;
                  return Boolean(
                    d.folders ||
                    d.integrations ||
                    d.chatbot ||
                    d.permissions ||
                    d.profile,
                  );
                }}
                pagination={
                  totalItems > 0 ? (
                    <Box
                      sx={{ borderTop: "1px solid", borderColor: "divider" }}
                    >
                      <PaginationControls
                        currentLimit={pageSize}
                        onLimitChange={(limit) => {
                          setPageSize(limit);
                          setPage(1);
                        }}
                        totalItems={totalItems}
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        showLimitSelector={true}
                        showPageInfo={true}
                        disabled={isFetching}
                        size="small"
                        pageTokens={[]}
                        hasNextPage={page < totalPages}
                        borderStyle={{ borderTop: "none" }}
                      />
                    </Box>
                  ) : null
                }
              />
            ) : (
              <BackendUserTable
                users={backendUsers}
                isLoading={isBackendLoading}
                isFetching={isBackendFetching}
                selectedUserId={selectedUserId ?? undefined}
                onManage={handleManageBackend}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
                pagination={
                  backendTotalItems > 0 ? (
                    <Box
                      sx={{ borderTop: "1px solid", borderColor: "divider" }}
                    >
                      <PaginationControls
                        currentLimit={pageSize}
                        onLimitChange={(limit) => {
                          setPageSize(limit);
                          setPage(1);
                        }}
                        totalItems={backendTotalItems}
                        currentPage={page}
                        totalPages={backendTotalPages}
                        onPageChange={setPage}
                        showLimitSelector={true}
                        showPageInfo={true}
                        disabled={isBackendFetching}
                        size="small"
                        pageTokens={[]}
                        hasNextPage={page < backendTotalPages}
                        borderStyle={{ borderTop: "none" }}
                      />
                    </Box>
                  ) : null
                }
              />
            )}
          </Box>

          {panelOpen && !isMobile && mode === "web" && (
            <UserManagementPanel
              key={`${createMode ? "n" : (selectedUserId ?? "x")}-${panelResetKey}`}
              user={panelUser}
              onClose={closePlatformPanel}
              onCloseRequest={requestClosePlatformPanel}
              onUnsavedChange={onPlatformUnsavedChange}
              onUserUpdated={(updated) => {
                createdUserRef.current = updated;
                createdAtRef.current = Date.now();
                setSelectedUserId(updated.id);
                setCreateMode(false);
                try {
                  window.localStorage.setItem(
                    PLATFORM_SELECTED_USER_STORAGE_KEY,
                    String(updated.id),
                  );
                } catch {}
              }}
              onUserArchived={handleUserArchived}
              onSavingChange={setIsSaving}
              drafts={drafts}
              onDraftsChange={handlePlatformDraftsChange}
            />
          )}
          {panelOpen && !isMobile && mode === "backend" && (
            <BackendUserPanel
              user={
                selectedUserId == null
                  ? null
                  : (backendUsers.find((u) => u.id === selectedUserId) ?? null)
              }
              onClose={closeBackendPanel}
              onUserUpdated={(updated) => {
                setSelectedUserId(updated.id);
                try {
                  window.localStorage.setItem(
                    BACKEND_SELECTED_KEY,
                    String(updated.id),
                  );
                } catch {}
              }}
              onUserDeleted={() => {
                showToast(
                  "User deleted. S3 cleanup has been queued.",
                  "success",
                );
              }}
            />
          )}
        </Box>

        <Dialog
          fullScreen
          open={Boolean(isMobile && panelOpen)}
          onClose={handleMobilePanelDialogClose}
          PaperProps={{
            sx: {
              m: 0,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            },
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {panelOpen && mode === "web" && (
              <UserManagementPanel
                key={`m-${createMode ? "n" : (selectedUserId ?? "x")}-${panelResetKey}`}
                fillViewport
                user={panelUser}
                onClose={closePlatformPanel}
                onCloseRequest={requestClosePlatformPanel}
                onUnsavedChange={onPlatformUnsavedChange}
                onUserUpdated={(updated) => {
                  createdUserRef.current = updated;
                  createdAtRef.current = Date.now();
                  setSelectedUserId(updated.id);
                  setCreateMode(false);
                  try {
                    window.localStorage.setItem(
                      PLATFORM_SELECTED_USER_STORAGE_KEY,
                      String(updated.id),
                    );
                  } catch {}
                }}
                onUserArchived={handleUserArchived}
                onSavingChange={setIsSaving}
                drafts={drafts}
                onDraftsChange={handlePlatformDraftsChange}
              />
            )}
            {panelOpen && mode === "backend" && (
              <BackendUserPanel
                fillViewport
                user={
                  selectedUserId == null
                    ? null
                    : (backendUsers.find((u) => u.id === selectedUserId) ??
                      null)
                }
                onClose={closeBackendPanel}
                onUserUpdated={(updated) => {
                  setSelectedUserId(updated.id);
                  try {
                    window.localStorage.setItem(
                      BACKEND_SELECTED_KEY,
                      String(updated.id),
                    );
                  } catch {}
                }}
                onUserDeleted={() => {
                  showToast(
                    "User deleted. S3 cleanup has been queued.",
                    "success",
                  );
                }}
              />
            )}
          </Box>
        </Dialog>

        <Drawer
          anchor="bottom"
          open={isMobile && mobileOptionsDrawerOpen}
          onClose={() => setMobileOptionsDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 2,
              borderTopRightRadius: 2,
              maxHeight: "min(88vh, 560px)",
              px: 2,
              py: 2,
            },
          }}
        >
          <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2 }}>
            Options
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Tooltip
              title={
                !canCreateUsers ? "Super User required to create users." : ""
              }
            >
              <span>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    if (!canCreateUsers) return;
                    const run = () => {
                      createdUserRef.current = null;
                      createdAtRef.current = null;
                      clearPersistedPlatformUserSelection();
                      setCreateMode(true);
                      setSelectedUserId(null);
                      setMobileOptionsDrawerOpen(false);
                    };
                    if (
                      mode === "web" &&
                      panelOpen &&
                      platformPanelUnsaved &&
                      (!createMode || selectedUserId !== null)
                    ) {
                      pendingLeaveDraftKeyRef.current = createMode
                        ? CREATE_MODE_DRAFT_KEY
                        : selectedUserId!;
                      pendingLeaveActionRef.current = run;
                      setLeaveDialogOpen(true);
                      return;
                    }
                    run();
                  }}
                  disabled={!canCreateUsers}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    minHeight: 44,
                    borderRadius: 1,
                  }}
                >
                  + New User
                </Button>
              </span>
            </Tooltip>

            {isSuperUser && (
              <ToggleButtonGroup
                fullWidth
                value={mode}
                exclusive
                onChange={(_, value) => {
                  if (!value) return;
                  const apply = () => {
                    setMode(value);
                    try {
                      window.localStorage.setItem(MODE_STORAGE_KEY, value);
                      if (value === "backend") {
                        clearPersistedPlatformUserSelection();
                      } else {
                        window.localStorage.removeItem(BACKEND_SELECTED_KEY);
                      }
                    } catch {}
                    setSelectedUserId(null);
                    setCreateMode(false);
                    setPage(1);
                    setRoleFilter(undefined);
                    setBackendLabelFilter(undefined);
                    setMobileOptionsDrawerOpen(false);
                  };
                  if (mode === "web" && panelOpen && platformPanelUnsaved) {
                    pendingLeaveDraftKeyRef.current = createMode
                      ? CREATE_MODE_DRAFT_KEY
                      : selectedUserId!;
                    pendingLeaveActionRef.current = apply;
                    setLeaveDialogOpen(true);
                    return;
                  }
                  apply();
                }}
                size="small"
                sx={{
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    py: 1,
                  },
                }}
              >
                <ToggleButton value="web">Web Users</ToggleButton>
                <ToggleButton value="backend">Backend Users</ToggleButton>
              </ToggleButtonGroup>
            )}

            <Divider />

            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={700}
                sx={{ display: "block", mb: 1.25 }}
              >
                {mode === "web" ? "Filter by role" : "Filter by label"}
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {mode === "web"
                  ? (
                      [
                        { value: "data_storage", label: "Data Storage" },
                        {
                          value: "integration_dashboard",
                          label: "Integration",
                        },
                        { value: "ai_agent", label: "AI Agent" },
                        { value: "calibrate_manager", label: "Manager" },
                        { value: "superuser", label: "Super Admin" },
                        { value: "top_folder_zip", label: "Top Folder ZIP" },
                        { value: "archived_pending", label: "Archived pending" },
                        { value: "archived", label: "Archived" },
                      ] as const
                    ).map(({ value, label }) => (
                      <Chip
                        key={value}
                        label={label}
                        size="small"
                        clickable
                        onClick={() => {
                          setRoleFilter((prev) =>
                            prev === value ? undefined : value,
                          );
                          setPage(1);
                          setMobileOptionsDrawerOpen(false);
                        }}
                        sx={{
                          fontWeight: 600,
                          fontSize: 12,
                          bgcolor:
                            roleFilter === value ? "primary.main" : undefined,
                          color:
                            roleFilter === value
                              ? "primary.contrastText"
                              : undefined,
                          "& .MuiChip-label": { px: 1.25 },
                        }}
                      />
                    ))
                  : (
                      [
                        { key: "migration" as const, label: "Migration" },
                        { key: "integration" as const, label: "Integration" },
                      ] as const
                    ).map(({ key, label }) => (
                      <Chip
                        key={key}
                        label={label}
                        size="small"
                        clickable
                        onClick={() => {
                          setBackendLabelFilter((prev) =>
                            prev === key ? undefined : key,
                          );
                          setPage(1);
                          setMobileOptionsDrawerOpen(false);
                        }}
                        sx={{
                          fontWeight: 600,
                          fontSize: 12,
                          bgcolor:
                            backendLabelFilter === key
                              ? "primary.main"
                              : undefined,
                          color:
                            backendLabelFilter === key
                              ? "primary.contrastText"
                              : undefined,
                          "& .MuiChip-label": { px: 1.25 },
                        }}
                      />
                    ))}
              </Box>
            </Box>
          </Box>
        </Drawer>

        <UnsavedPlatformDraftsDialog
          open={staleDraftsDialogOpen}
          onClose={() => setStaleDraftsDialogOpen(false)}
          title="Unsaved changes from last time"
          intro="You have unsaved changes. Discard them to continue or stay to keep editing."
          rows={staleDraftSummaryRows}
          onDiscardAndContinue={() => {
            clearPlatformUserDrafts();
            setDrafts({});
            setStaleDraftsDialogOpen(false);
            closePlatformPanel();
            setPanelResetKey((k) => k + 1);
          }}
        />

        <UnsavedPlatformDraftsDialog
          open={leaveDialogOpen}
          onClose={() => {
            setLeaveDialogOpen(false);
            pendingLeaveDraftKeyRef.current = null;
            pendingLeaveActionRef.current = null;
          }}
          title="Unsaved changes"
          intro="You have unsaved changes. Discard them to continue or stay to keep editing."
          rows={leaveDraftSummaryRows}
          discardButtonLabel="Discard and continue"
          onDiscardAndContinue={handleDiscardAndLeavePlatform}
        />
      </Container>
    </Layout>
  );
}
